import { reactive, ref, nextTick, type Ref } from "vue";
import { useRouter } from "vue-router";
import type { ApolloQueryResult } from "@apollo/client/core";
import type { ExecutionResult } from "graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { getTmpMember } from "@/common/graphql/tmpMember";
import { routerPath } from "@/common/router";
import deliveryRegisterInputStore from "@/store/deliveryRegisterInputStore";
import type {
  OperationInput,
  MemberUpdateInput,
  MemberCreateInput,
  UpdateMemberMutation,
  UpdateMemberMutationVariables,
  ListDeliveryAvailabilityQuery,
  ListDeliveryAvailabilityQueryVariables,
} from "@/generated/graphql";
import {
  UpdateMemberDocument,
  ListDeliveryAvailabilityDocument,
} from "@/generated/graphql";
import type { DeliveryAvailabilityData } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseTmpMemberActionsParams {
  notdeliverableerrdialog: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  editPage: Ref<boolean>;
  overlay: Ref<boolean>;
  openListPage: () => void;
  searchQuery: () => Promise<void>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useTmpMemberActions = (params: UseTmpMemberActionsParams) => {
  const {
    notdeliverableerrdialog,
    errdialog,
    errMessage,
    editPage,
    overlay,
    openListPage,
    searchQuery,
  } = params;

  const router = useRouter();

  // 配達可能情報
  const getAvailableDate = ref("");
  const deliveryAvailabilityStore = ref<DeliveryAvailabilityData[][]>([]);
  const btnDisabled = ref(true);

  // 編集画面
  const editTmpMember = ref<MemberUpdateInput | null>(null);

  const memberUpdateInput = reactive<MemberUpdateInput>({
    customer_code: "", chain_code: "", address1: "", address2: "", address3: "",
    member_id: "", name1: "", name2: "", name_kana1: "", name_kana2: "",
    search_kana: "", notes: "", phone: "", shop_code: "", zip_code: "", house_card: "",
  });

  const operationInput = reactive<OperationInput>({
    area_code: "10", detail: "操作詳細", display_category: "画面名",
    operation: "操作", user_id: localStorageGetUserId(),
  });

  const getDeliveryAvailable = async (): Promise<void> => {
    const apolloClient = await generateApolloClient();
    const resultAvailability: ApolloQueryResult<ListDeliveryAvailabilityQuery> = await apolloClient.query<ListDeliveryAvailabilityQuery, ListDeliveryAvailabilityQueryVariables>({
      query: ListDeliveryAvailabilityDocument,
      variables: { customer_code: localStorageGetCustomerCode(), chain_code: localStorageGetChainCode(), shop_code: localStorageGetShopCode() },
      fetchPolicy: "no-cache",
    });
    const list = resultAvailability.data.listDeliveryAvailability;
    if (list === undefined || list === null) return;
    const _date = new Date();
    getAvailableDate.value = _date.getDate().toString();
    let currentDate = "";
    const allAvialibity: DeliveryAvailabilityData[] = [];
    let idx = 0;
    list.forEach((value) => {
      if (value !== undefined && value !== null) {
        value.delivery_date = value.delivery_date.substring(0, 10);
        if (currentDate.length == 0 || currentDate != value.delivery_date) { currentDate = value.delivery_date; idx = 0; }
        allAvialibity.push({ key: ++idx, delivery_id: value.delivery_id, delivery_id_desc: value.delivery_id_desc, delivery_date: value.delivery_date, availability: value.availability.trim(), area_code: value.area_code, delivery_start_time: value.delivery_start_time, delivery_end_time: value.delivery_end_time });
      }
    });
    deliveryAvailabilityStore.value = [];
    deliveryAvailabilityStore.value.push(allAvialibity);
    btnDisabled.value = false;
  };

  const onClickDeliveryRequestByTmpMember = async (tmpMemberId: string, is_deliverable: number): Promise<void> => {
    deliveryRegisterInputStore.setupValue({
      zip_code: "", pref_name: "", user_id: "", areaDialog: false,
      getAvailableDate: getAvailableDate.value,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      tmp_member_id: tmpMemberId,
    });
    if (is_deliverable == 0) { notdeliverableerrdialog.value = true; return; }
    router.push({ path: routerPath.deliveryRequestRegisterInput });
  };

  const onClickMemberRegister = async (tmpMemberId: string): Promise<void> => {
    deliveryRegisterInputStore.setupValue({
      zip_code: "", pref_name: "", user_id: "", areaDialog: false,
      getAvailableDate: getAvailableDate.value,
      deliveryJson: JSON.stringify(deliveryAvailabilityStore.value),
      tmp_member_id: tmpMemberId,
    });
    router.push({ path: routerPath.memberRegister });
  };

  const onClickEdit = async (tmpMemberId: string): Promise<void> => {
    const logInput: OperationInput = { area_code: "10", detail: "操作詳細", display_category: "画面名", operation: "操作", user_id: localStorageGetUserId() };
    editTmpMember.value = (await getTmpMember(tmpMemberId, logInput)) as unknown as MemberUpdateInput;
    editPage.value = true;
  };

  const updateMember = async (): Promise<void> => {
    if (editTmpMember.value === null) return;
    memberUpdateInput.customer_code = localStorageGetCustomerCode();
    memberUpdateInput.chain_code = localStorageGetChainCode();
    memberUpdateInput.address1 = editTmpMember.value.address1;
    memberUpdateInput.address2 = editTmpMember.value.address2;
    memberUpdateInput.address3 = editTmpMember.value.address3;
    memberUpdateInput.member_id = editTmpMember.value.member_id;
    memberUpdateInput.name1 = editTmpMember.value.name1;
    memberUpdateInput.name2 = editTmpMember.value.name2;
    memberUpdateInput.name_kana1 = editTmpMember.value.name_kana1;
    memberUpdateInput.name_kana2 = editTmpMember.value.name_kana2;
    memberUpdateInput.search_kana = editTmpMember.value.name_kana1 + editTmpMember.value.name_kana2;
    memberUpdateInput.notes = editTmpMember.value.notes;
    memberUpdateInput.phone = editTmpMember.value.phone;
    memberUpdateInput.shop_code = editTmpMember.value.shop_code;
    memberUpdateInput.zip_code = editTmpMember.value.zip_code;
    memberUpdateInput.house_card = editTmpMember.value.house_card;
    const apolloClient = await generateApolloClient();
    const result: ExecutionResult<UpdateMemberMutation> = await apolloClient.mutate<UpdateMemberMutation, UpdateMemberMutationVariables>({
      mutation: UpdateMemberDocument,
      variables: { input: memberUpdateInput, logInput: operationInput },
    });
  };

  const onClickConfirmUpdateOk = async (): Promise<void> => {
    overlay.value = true;
    try {
      await updateMember();
      editTmpMember.value = null;
      openListPage();
    } catch (err: unknown) {
      console.error(err);
      overlay.value = false;
      errdialog.value = true;
      errMessage.value = "会員の編集に失敗しました。";
      return;
    }
    await nextTick();
    await searchQuery();
    overlay.value = false;
  };

  return {
    getAvailableDate,
    deliveryAvailabilityStore,
    btnDisabled,
    editTmpMember,
    getDeliveryAvailable,
    onClickDeliveryRequestByTmpMember,
    onClickMemberRegister,
    onClickEdit,
    onClickConfirmUpdateOk,
  };
};
